/**
 * ASL Control Plane - Theme Presets & Configuration Constants
 */

export const THEME_PRESETS = [
  {
    id: 'asl_classic',
    name: 'ASL Standard Blau',
    description: 'Klassisches professionelles B2B-Design mit Sicherheitsblau & Orange-Akzenten',
    primaryColor: '#0056b3',
    secondaryColor: '#5f5e5f',
    accentColor: '#ff9900',
    borderRadius: '0.375rem',
    fontFamily: 'Inter, sans-serif',
  },
  {
    id: 'security_dark_amber',
    name: 'Industrial Dark & Amber',
    description: 'Dunkles Schiefergrau mit markanten Industrie-Warnfarben',
    primaryColor: '#1e293b',
    secondaryColor: '#64748b',
    accentColor: '#f59e0b',
    borderRadius: '0.25rem',
    fontFamily: 'Inter, sans-serif',
  },
  {
    id: 'modern_emerald',
    name: 'Modern Emerald',
    description: 'Hochwertiges Smaragdgrün für innovative Sicherheitstechnik',
    primaryColor: '#065f46',
    secondaryColor: '#475569',
    accentColor: '#10b981',
    borderRadius: '0.5rem',
    fontFamily: 'Inter, sans-serif',
  },
  {
    id: 'crimson_navy',
    name: 'Corporate Crimson & Navy',
    description: 'Seriöses Dunkelblau mit feurigen roten Akzenten',
    primaryColor: '#0f172a',
    secondaryColor: '#334155',
    accentColor: '#dc2626',
    borderRadius: '0.375rem',
    fontFamily: 'Inter, sans-serif',
  },
  {
    id: 'nordic_cyan',
    name: 'Nordic Cyan / High-Tech',
    description: 'Frisches skandinavisches Cyan für Smarthome & Digital-Security',
    primaryColor: '#0e7490',
    secondaryColor: '#64748b',
    accentColor: '#06b6d4',
    borderRadius: '0.75rem',
    fontFamily: 'Inter, sans-serif',
  },
  {
    id: 'minimal_monochrome',
    name: 'Minimal Monochrome',
    description: 'Reduziertes Schwarz/Grau mit dezenten Kontrasten',
    primaryColor: '#18181b',
    secondaryColor: '#71717a',
    accentColor: '#3b82f6',
    borderRadius: '0.25rem',
    fontFamily: 'Inter, sans-serif',
  },
];

export const RADIUS_OPTIONS = [
  { label: 'Eckig (0px)', value: '0rem' },
  { label: 'Kompakt (4px)', value: '0.25rem' },
  { label: 'Standard (6px)', value: '0.375rem' },
  { label: 'Mittel (8px)', value: '0.5rem' },
  { label: 'Groß (12px)', value: '0.75rem' },
  { label: 'Rund (16px)', value: '1rem' },
];

/**
 * Astryx Slot Component Configuration Options
 */
export const HEADER_SLOT_OPTIONS = [
  {
    value: 'slim',
    label: 'Slim Header',
    description: 'Kompakt & einzeilig, fokussiert auf Schnellsuche & Direktnavigation.',
    tag: 'Empfohlen für Sicherheitstechnik-Shops',
  },
  {
    value: 'enterprise',
    label: 'Enterprise Mega-Menu',
    description: 'Mehrzeilig mit Fachbereichen, Ansprechpartner-Hotline & B2B-Portal.',
    tag: 'Großes Sortiment',
  },
  {
    value: 'centered',
    label: 'Centered Brand',
    description: 'Zentriertes Marken-Logo mit symmetrischer Produktnavigation.',
    tag: 'Fokus auf Partner-Brand',
  },
];

export const HERO_SLOT_OPTIONS = [
  {
    value: 'portal_search',
    label: 'Portal Suche',
    description: 'Zentrale Direktsuche mit SKU-Schnellfinder & Normen-Kategorien.',
    tag: 'Effizienz-Fokus',
  },
  {
    value: 'highlight_grid',
    label: 'Highlight Grid',
    description: '3-Kachel-Übersicht für Brandschutz, Videotechnik & Einbruchmelder.',
    tag: 'Kategorienschwerpunkt',
  },
  {
    value: 'split_featured',
    label: 'Split Spotlight',
    description: 'Geteiltes Layout mit Fokus auf Spitzen-Produkt & Errichter-Vorteile.',
    tag: 'Produkt-Highlight',
  },
];

export const CARD_SLOT_OPTIONS = [
  {
    value: 'specs_heavy',
    label: 'Technische Details',
    description: 'Ausführliche Spezifikationen, VdS/DIN-Normen & Hersteller-SKUs.',
    tag: 'Techniker & Planer',
  },
  {
    value: 'compact_row',
    label: 'Kompakte Zeile',
    description: 'Dichte Tabellenansicht mit Mengenauswahl für schnelle Großbestellungen.',
    tag: 'Schnellbestellungen',
  },
  {
    value: 'visual_card',
    label: 'Visuelle Bildkarte',
    description: 'Große Bildvorschau mit Hover-Effekten & optischer Hervorhebung.',
    tag: 'Präsentations-Fokus',
  },
];

export const FILTER_SLOT_OPTIONS = [
  {
    value: 'sidebar',
    label: 'Sidebar Filterbaum',
    description: 'Klassischer linker Navigationsbaum mit hierarchischer Filterung.',
    tag: 'Klassisch',
  },
  {
    value: 'topbar',
    label: 'Horizontale Topbar',
    description: 'Moderne Filter-Chips & Dropdowns oberhalb der Produktliste.',
    tag: 'Modern & Luftig',
  },
];

export const SLOT_OPTIONS = {
  header: HEADER_SLOT_OPTIONS,
  hero: HERO_SLOT_OPTIONS,
  card: CARD_SLOT_OPTIONS,
  filter: FILTER_SLOT_OPTIONS,
};

export const INQUIRY_STATUS_MAP = {
  new: { label: 'Neu', badgeVariant: 'success', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  in_review: { label: 'In Prüfung', badgeVariant: 'warning', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  quoted: { label: 'Angebot erstellt', badgeVariant: 'accent', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  accepted: { label: 'Angenommen', badgeVariant: 'success', color: 'text-emerald-700 bg-emerald-100 border-emerald-300' },
  rejected: { label: 'Abgelehnt', badgeVariant: 'destructive', color: 'text-rose-700 bg-rose-50 border-rose-200' },
  archived: { label: 'Archiviert', badgeVariant: 'secondary', color: 'text-slate-600 bg-slate-100 border-slate-200' },
};

/**
 * Calculates WCAG 2.1 relative luminance for a given hex color.
 */
export function calculateLuminance(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return 0;
  const clean = hexColor.replace('#', '');
  if (clean.length !== 6) return 0;
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  const a = [r, g, b].map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

/**
 * Computes contrast ratio against white (#ffffff) or dark (#1a1c1c).
 */
export function getContrastRatio(hexColor, bgHex = '#ffffff') {
  const lum1 = calculateLuminance(hexColor);
  const lum2 = calculateLuminance(bgHex);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  const ratio = (brightest + 0.05) / (darkest + 0.05);
  return Number(ratio.toFixed(2));
}

/**
 * Helper to calculate selling price with markup in integer cents.
 */
export function calculatePriceWithMarkup(basePriceCents, markupPercent = 0) {
  const base = Math.max(0, parseInt(basePriceCents, 10) || 0);
  const percent = parseFloat(markupPercent) || 0;
  const factor = 1 + percent / 100;
  return Math.round(base * factor);
}
